import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Phone, MessageCircle, MessageSquare, AlertTriangle, Clock, Calendar, FileX, Wallet, Sparkles, ArrowRight, ShieldAlert, Lock, ShieldCheck, Headphones } from 'lucide-react';
import { toast } from 'sonner';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { StatusChip } from '@/components/shared/StatusChip';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useApp } from '@/context/AppContext';
import { formatSAR } from '@/lib/format';
import { evaluateAction } from '@/lib/compliance';
import { usePageTitle } from '@/lib/usePageTitle';
import { useConfirm } from '@/components/shared/ConfirmDialog';
import { cn } from '@/lib/utils';

const SECTIONS = [
  { id: 'priority',  title: 'Priority queue',          icon: Sparkles,    tone: 'primary',     hint: 'Highest-impact open cases right now' },
  { id: 'ptp',       title: 'Promises due today',      icon: Calendar,    tone: 'success',     hint: 'Follow up before EOD to keep promise rate' },
  { id: 'callbacks', title: 'Callbacks scheduled',     icon: Clock,       tone: 'info',        hint: 'Debtors who asked to be called back' },
  { id: 'broken',    title: 'Broken promises',         icon: FileX,       tone: 'destructive', hint: 'Renegotiate or escalate' },
  { id: 'high_value', title: 'High-value · no contact', icon: Wallet,     tone: 'warning',     hint: 'SAR > 50K, no contact in 5+ days' },
  { id: 'disputes',  title: 'Dispute / hardship hold', icon: ShieldAlert, tone: 'warning',     hint: 'Contact paused — document & monitor' },
];

const groupCases = (debtors) => {
  return {
    priority:   debtors.filter((d) => d.scores.priority >= 80 && !d.holds.dispute && !d.holds.hardship).slice(0, 6),
    ptp:        debtors.filter((d) => d.status === 'Promise to Pay').slice(0, 4),
    callbacks:  debtors.filter((d) => d.status === 'Contact Scheduled').slice(0, 4),
    broken:     debtors.filter((d) => d.status === 'Broken Promise').slice(0, 4),
    high_value: debtors.filter((d) => d.amount > 50000 && (d.contactStats?.lastContactDaysAgo ?? 0) >= 5).slice(0, 4),
    disputes:   debtors.filter((d) => d.holds.dispute || d.holds.hardship).slice(0, 4),
  };
};

export default function AgentWorkspace() {
  usePageTitle('Agent Workbench');
  const navigate = useNavigate();
  const { debtors } = useApp();
  const confirm = useConfirm();
  const [q, setQ] = useState('');
  const [active, setActive] = useState('all'); // all | <section.id>

  const grouped = useMemo(() => groupCases(debtors), [debtors]);

  const handleAutoDial = async () => {
    const queue = grouped.priority.filter((d) => evaluateAction(d, 'call').allowed);
    const next = queue[0];
    if (!next) {
      toast.error('No callable debtor in queue', { description: 'All priority cases are blocked by compliance rules.' });
      return;
    }
    const ok = await confirm({
      title: 'Auto-dial next priority case?',
      description: 'The system will place an outbound call to the highest-priority compliance-clear debtor in your queue and open their case file.',
      impactSummary: [
        { label: 'Debtor', value: next.name },
        { label: 'Case', value: next.caseId },
        { label: 'Exposure', value: formatSAR(next.amount) },
        { label: 'Priority score', value: next.scores.priority },
      ],
      confirmLabel: `Call ${next.name}`,
      tone: 'warning',
    });
    if (ok) {
      toast.success(`Dialling ${next.name}…`, { description: `Opening ${next.caseId}` });
      navigate(`/app/agent/case/${next.caseId}`);
    }
  };

  const focusKpis = useMemo(() => ([
    { l: 'Calls done', v: 11, t: '+4 vs avg' },
    { l: 'Promises kept', v: '78%', t: '+6 pts' },
    { l: 'RPC rate', v: '54%', t: 'team 49%' },
    { l: 'SLA breach risk', v: 3, t: 'next 2h' },
    { l: 'Compliance flags', v: 1, t: 'review' },
  ]), []);

  const visibleSections = active === 'all' ? SECTIONS : SECTIONS.filter((s) => s.id === active);

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader
        eyebrow="Agent workbench"
        title="Today's focus — task-led queue"
        subtitle="Cases grouped by what needs to happen right now. Every action is gated by SAMA & PDPL rules in real time."
        actions={(
          <>
            <Button variant="outline" size="sm" data-testid="workbench-filters"><Filter className="h-4 w-4 mr-1.5" />Filters</Button>
            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="workbench-autodial" onClick={handleAutoDial}>Auto-dial next</Button>
          </>
        )}
      />

      {/* Daily focus strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="workbench-kpi-strip">
        {focusKpis.map((s) => (
          <div key={s.l} className="surface-card p-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.l}</p>
            <p className="font-mono-num text-xl font-semibold mt-1">{s.v}</p>
            <p className="text-[10px] text-success mt-0.5">{s.t}</p>
          </div>
        ))}
      </div>

      {/* Section tabs + search */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <Tabs value={active} onValueChange={setActive}>
          <TabsList className="bg-muted h-9 flex-wrap">
            <TabsTrigger value="all" className="text-xs" data-testid="tab-all">All sections</TabsTrigger>
            {SECTIONS.map((s) => (
              <TabsTrigger key={s.id} value={s.id} className="text-xs" data-testid={`tab-${s.id}`}>
                {s.title}
                <Badge variant="outline" className="ml-1.5 text-[10px]">{grouped[s.id]?.length || 0}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search case / debtor" className="pl-9 h-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="workbench-search" />
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-6">
        {visibleSections.map((section) => {
          let cases = grouped[section.id] || [];
          if (q) cases = cases.filter((d) => (d.name + d.caseId).toLowerCase().includes(q.toLowerCase()));
          if (!cases.length) return (
            <EmptySection key={section.id} section={section} />
          );
          return (
            <WorkbenchSection
              key={section.id}
              section={section}
              cases={cases}
              onOpen={(c) => navigate(`/app/agent/case/${c.caseId}`)}
            />
          );
        })}
      </div>
    </div>
  );
}

const WorkbenchSection = ({ section, cases, onOpen }) => {
  const Icon = section.icon;
  return (
    <div className="surface-card overflow-hidden" data-testid={`section-${section.id}`}>
      <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-muted/40">
        <div className="flex items-center gap-3">
          <div className={cn(
            'h-8 w-8 rounded-md flex items-center justify-center',
            section.tone === 'primary' && 'bg-primary/15 text-gold-deep',
            section.tone === 'success' && 'bg-success/15 text-success',
            section.tone === 'info' && 'bg-info/15 text-info',
            section.tone === 'warning' && 'bg-warning/15 text-warning',
            section.tone === 'destructive' && 'bg-destructive/15 text-destructive',
          )}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="leading-tight">
            <p className="text-sm font-semibold">{section.title}</p>
            <p className="text-[11px] text-muted-foreground">{section.hint}</p>
          </div>
        </div>
        <Badge variant="outline" className="text-[10px]">{cases.length} case{cases.length === 1 ? '' : 's'}</Badge>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
        {cases.map((d) => (
          <CaseCard key={d.caseId} d={d} onOpen={() => onOpen(d)} />
        ))}
      </div>
    </div>
  );
};

const CaseCard = ({ d, onOpen }) => {
  const callV = evaluateAction(d, 'call');
  const waV = evaluateAction(d, 'whatsapp');
  const smsV = evaluateAction(d, 'sms');

  const worst = [callV, waV, smsV].reduce((a, b) => (a.severity.weight > b.severity.weight ? a : b), callV);
  const blocked = !callV.allowed && !waV.allowed && !smsV.allowed;

  return (
    <div
      className={cn(
        'group rounded-xl border bg-card p-4 hover:shadow-md transition-all cursor-pointer relative',
        worst.severity.weight > 0
          ? worst.severity.tone === 'destructive' ? 'border-destructive/40' : 'border-warning/50'
          : 'border-border hover:border-primary/40'
      )}
      onClick={onOpen}
      data-testid={`case-card-${d.caseId}`}
    >
      {/* B3 — sticky SAMA chip elevated to card header */}
      {worst.severity.weight > 0 && (
        <div className={cn(
          'absolute -top-2 left-3 px-2 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold flex items-center gap-1 shadow-sm',
          worst.severity.tone === 'destructive'
            ? 'bg-destructive text-destructive-foreground'
            : 'bg-warning text-warning-foreground'
        )}>
          {blocked ? <Lock className="h-2.5 w-2.5" aria-hidden="true" /> : <ShieldAlert className="h-2.5 w-2.5" aria-hidden="true" />}
          {worst.rule}
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-9 w-9 rounded-full bg-primary/15 text-gold-deep flex items-center justify-center text-[11px] font-semibold shrink-0" aria-hidden="true">
            {d.name.split(' ').map((w) => w[0]).join('')}
          </div>
          <div className="min-w-0">
            <p className="font-medium leading-tight truncate">{d.name}</p>
            <p className="text-[11px] text-muted-foreground truncate">{d.caseId} · {d.region} · {d.product}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-mono-num text-sm font-semibold">{formatSAR(d.amount)}</p>
          <p className={cn('text-[11px] font-mono-num', d.delinquencyDays > 90 ? 'text-destructive' : d.delinquencyDays > 60 ? 'text-warning' : 'text-muted-foreground')}>{d.delinquencyDays}d overdue</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <StatusChip status={d.status} />
        <div className="flex items-center gap-1 ml-auto">
          <div className="h-1.5 w-14 rounded-full bg-muted overflow-hidden" role="progressbar" aria-label="Priority score" aria-valuenow={d.scores.priority} aria-valuemin="0" aria-valuemax="100">
            <div className="h-full bg-primary" style={{ width: `${d.scores.priority}%` }} />
          </div>
          <span className="font-mono-num text-[11px] text-muted-foreground">{d.scores.priority}</span>
        </div>
      </div>

      <div className="mt-3 rounded-md bg-muted/40 p-2.5 text-[11px] leading-snug">
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles className="h-3 w-3 text-primary" aria-hidden="true" />
          <span className="uppercase tracking-widest text-[9px] text-primary">Next best action</span>
        </div>
        <p className="text-foreground">{d.nextBestAction}</p>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <ActionPill icon={Phone} verdict={callV} />
          <ActionPill icon={MessageCircle} verdict={waV} />
          <ActionPill icon={MessageSquare} verdict={smsV} />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-primary text-[11px] h-7"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          data-testid={`case-open-${d.caseId}`}
        >
          Open case <ArrowRight className="h-3 w-3 ml-1" aria-hidden="true" />
        </Button>
      </div>

      {/* Footer ribbon — full explanation */}
      {worst.severity.weight > 0 && (
        <div className={cn(
          'mt-3 -mx-4 -mb-4 px-4 py-1.5 text-[10px] flex items-center gap-1.5 border-t',
          worst.severity.tone === 'destructive' && 'bg-destructive/8 text-destructive border-destructive/30',
          worst.severity.tone === 'warning' && 'bg-warning/8 text-warning border-warning/40',
        )}>
          {blocked ? <Lock className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
          <span className="font-medium uppercase tracking-widest text-[9px]">{worst.rule}</span>
          <span className="text-muted-foreground truncate">— {worst.reason}</span>
        </div>
      )}
    </div>
  );
};

const ActionPill = ({ icon: Icon, verdict }) => {
  const blocked = !verdict.allowed;
  return (
    <span
      title={verdict.reason}
      className={cn(
        'h-6 w-6 inline-flex items-center justify-center rounded-md border',
        blocked
          ? 'border-destructive/30 bg-destructive/8 text-destructive/70'
          : verdict.severity.weight > 0
            ? 'border-warning/40 bg-warning/10 text-warning'
            : 'border-border bg-muted text-foreground'
      )}
    >
      <Icon className="h-3 w-3" />
    </span>
  );
};

const EmptySection = ({ section }) => (
  <div className="surface-card p-6 text-center" data-testid={`empty-${section.id}`}>
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{section.title}</p>
    <p className="text-sm mt-1">Nothing here right now — caught up.</p>
  </div>
);
